/**
 * DesignLibraryPage  — /designs
 *
 * Public browse page showing the full card design catalog.
 * Users can filter by occasion type and see a live CSS-rendered preview
 * of every available design.
 *
 * Linked from DashboardNav and the occasion form "Browse all designs" shortcut.
 */

import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import CardDesignPreview, { type DesignData } from '../components/cards/CardDesignPreview';
import CardInsidePreview from '../components/cards/CardInsidePreview';
import CardMailShowcase from '../components/cards/CardMailShowcase';
import DashboardNav from '../components/dashboard/DashboardNav';
import LandingNav from '../components/LandingNav';
import Footer from '../components/Footer';
import { apiFetch } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { sampleGreetingForOccasion } from '../lib/greeting';
import PageSEO from '../components/PageSEO';

type StyleFilter = 'all' | 'elegant' | 'floral' | 'modern' | 'sentimental' | 'luxury' | 'religious';

const OCCASION_TABS = [
  { key: '',               label: 'All'             },
  { key: 'birthday',       label: '🎂 Birthday'     },
  { key: 'anniversary',    label: '💍 Anniversary'  },
  { key: 'christmas',      label: '🎄 Christmas'    },
  { key: 'valentines',     label: '💝 Valentine\'s' },
  { key: 'mothers_day',    label: '🌸 Mother\'s Day'},
  { key: 'fathers_day',    label: '👔 Father\'s Day'},
  { key: 'graduation',     label: '🎓 Graduation'   },
  { key: 'thinking_of_you',label: '💌 Just Because' },
];

const STYLE_OPTIONS: { key: StyleFilter; label: string }[] = [
  { key: 'all',         label: 'All styles'  },
  { key: 'luxury',      label: '✦ Luxury'    },
  { key: 'elegant',     label: 'Elegant'     },
  { key: 'floral',      label: 'Floral'      },
  { key: 'modern',      label: 'Modern'      },
  { key: 'sentimental', label: 'Sentimental' },
  { key: 'religious',   label: 'Religious'   },
];

// ── Design detail overlay ─────────────────────────────────────────────────────

// ── Print-line counter (46 chars ≈ one print line at 11pt on a 4.5" wide message area)
const CHARS_PER_PRINT_LINE = 45;
const MAX_PRINT_LINES      = 14;
const MAX_CHARS            = 650;

function countPrintLines(text: string): number {
  if (!text) return 0;
  return text.split('\n').reduce((total, line) => {
    return total + (line.length === 0 ? 1 : Math.ceil(line.length / CHARS_PER_PRINT_LINE));
  }, 0);
}

function DesignDetail({
  design,
  cardGreeting,
  cardClosing,
  cardFromLine,
  onClose,
}: {
  design:        DesignData;
  cardGreeting?: string;
  cardClosing?:  string;
  cardFromLine?: string;
  onClose:       () => void;
}) {
  const { profile } = useAuth();
  const [message,    setMessage   ] = useState('');
  const [showVirtual,setShowVirtual] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);


  // Auto-focus the textarea on open
  useEffect(() => {
    setTimeout(() => textareaRef.current?.focus(), 100);
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-[28px] shadow-2xl w-full flex flex-col gap-0 overflow-hidden"
        style={{ maxWidth: 680, maxHeight: '92vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header bar */}
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-100">
          <div>
            <p className="font-heading text-lg font-bold text-navy leading-tight">{design.name}</p>
            <p className="font-body text-xs text-gray-400 mt-0.5 capitalize">{design.style} · {design.tagline}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-300 hover:text-gray-500 transition-colors ml-4 flex-shrink-0"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Main two-column preview area */}
        <div className="flex flex-col sm:flex-row gap-0 flex-1 overflow-y-auto">

          {/* LEFT — Card front */}
          <div className="sm:w-[264px] flex-shrink-0 flex flex-col items-center justify-start
                          bg-gray-50 border-b sm:border-b-0 sm:border-r border-gray-100 p-6 gap-3">
            <p className="font-body text-[10px] font-semibold text-gray-400 uppercase tracking-widest self-start">
              Card Front
            </p>
            <CardDesignPreview design={design} size="md" showName={false} />
            <p className="font-body text-[10px] text-gray-400 text-center leading-relaxed">
              Printed on premium cardstock · Mailed directly to their door
            </p>
          </div>

          {/* RIGHT — Card inside with live message preview */}
          <div className="flex-1 flex flex-col gap-4 p-6 overflow-y-auto">
            <div className="flex items-center gap-2">
              <p className="font-body text-[10px] font-semibold text-gray-400 uppercase tracking-widest">
                Card Inside
              </p>
              <span className="font-body text-[10px] text-gray-300">·</span>
              <p className="font-body text-[10px] text-gray-400">
                Your message is printed here, not on the front
              </p>
            </div>

            {/* Live inside preview */}
            <CardInsidePreview
              design={design}
              message={message}
              cardGreeting={cardGreeting}
              cardClosing={cardClosing}
              cardFromLine={cardFromLine}
            />

            {/* Message textarea */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="font-body text-xs font-semibold text-navy">
                  Try a sample message
                </label>
              </div>
              <textarea
                ref={textareaRef}
                value={message}
                placeholder="Type anything to see exactly how your message will look printed inside…"
                rows={8}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val.length <= MAX_CHARS && countPrintLines(val) <= MAX_PRINT_LINES) {
                    setMessage(val);
                  }
                }}
                className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3
                           font-body text-sm text-navy placeholder-gray-300 resize-none
                           focus:outline-none focus:ring-2 focus:ring-sage/30 focus:border-sage/50
                           transition-colors"
              />
              {(() => {
                const lines   = countPrintLines(message);
                const warn    = lines > 6 || message.length > 280;
                const atLimit = lines >= MAX_PRINT_LINES || message.length >= MAX_CHARS;
                return (
                  <div className="flex items-center justify-between mt-1.5">
                    <p className="font-body text-[10px] text-gray-400">
                      Type your message above to preview how it looks inside the card
                    </p>
                    <p className={`font-body text-[10px] flex-shrink-0 ml-2 ${atLimit ? 'text-red-500' : warn ? 'text-amber-600' : 'text-gray-400'}`}>
                      {lines}/{MAX_PRINT_LINES} lines · {message.length}/{MAX_CHARS} chars
                      {atLimit ? ', limit reached' : warn ? ', near limit' : ''}
                    </p>
                  </div>
                );
              })()}
              {/* Virtual card tour button */}
              <button
                type="button"
                onClick={() => setShowVirtual(true)}
                className="mt-2 w-full flex items-center justify-center gap-2 rounded-xl
                           border border-dashed py-2.5 text-xs font-body font-semibold transition-colors
                           hover:bg-white"
                style={{ borderColor: design.accent_color + '60', color: design.accent_color }}
              >
                <span>✉️</span>
                Virtual card tour, front, inside &amp; back
              </button>
            </div>

            {/* Message tone */}
            <div className="rounded-xl bg-gray-50 px-4 py-3">
              <p className="font-body text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">
                Message tone for this design
              </p>
              <p className="font-body text-xs text-gray-600 leading-relaxed">
                {(design as DesignData & { message_tone?: string }).message_tone ?? 'Warm and thoughtful.'}
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between gap-3">
          <p className="font-body text-xs text-gray-400 hidden sm:block">
            Click any design in the grid to preview it here
          </p>
          <button onClick={onClose} className="btn-secondary text-sm py-2 px-6 ml-auto">
            Done
          </button>
        </div>
      </div>

      {/* Card mail showcase: front -> open -> envelope -> mailed */}
      {showVirtual && (
        <CardMailShowcase
          design={design}
          message={message}
          cardGreeting={cardGreeting}
          cardClosing={cardClosing}
          cardFromLine={cardFromLine}
          cardFontId={profile?.card_font_id ?? undefined}
          onClose={() => setShowVirtual(false)}
        />
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function DesignLibraryPage() {
  const { profile, user } = useAuth();
  const [designs,      setDesigns     ] = useState<(DesignData & { message_tone?: string })[]>([]);
  const [loading,      setLoading     ] = useState(true);
  const [occasionTab,  setOccasionTab ] = useState('');
  const [styleFilter,  setStyleFilter ] = useState<StyleFilter>('all');
  const [detail,       setDetail      ] = useState<DesignData | null>(null);

  const cardClosing   = profile?.card_closing   ?? 'With love,';
  const cardFromLine  = profile?.card_from_line ?? profile?.first_name ?? '';
  const cardGreeting  = sampleGreetingForOccasion(occasionTab);

  useEffect(() => {
    const params = new URLSearchParams();
    if (occasionTab) params.set('occasion_type', occasionTab);
    setLoading(true);
    apiFetch<{ designs: DesignData[] }>(`/api/designs?${params}`)
      .then((json) => setDesigns(json.designs ?? []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [occasionTab]);

  const filtered = styleFilter === 'all'
    ? designs
    : designs.filter((d) => d.style === styleFilter);

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <PageSEO
        title="Card Designs | Birthday, Anniversary and Holiday Cards | MomentSent"
        description="Browse MomentSent's full collection of greeting card designs for birthdays, anniversaries, Christmas, Valentine's Day, and more. Beautiful real cards mailed automatically for you."
        canonical="https://momentsent.com/designs"
      />

      {user ? <DashboardNav /> : <LandingNav />}

      <div className={`max-w-5xl mx-auto px-4 sm:px-6 py-10 pb-24 sm:pb-10 flex-1 ${!user ? 'pt-24' : ''}`}>

        {/* Context banner for unauthenticated visitors */}
        {!user && (
          <div className="mb-6 rounded-[16px] bg-navy/5 border border-navy/15 px-5 py-4 flex items-start gap-3">
            <span className="text-xl flex-shrink-0 mt-0.5">💌</span>
            <div>
              <p className="font-heading text-sm font-semibold text-navy">
                MomentSent mails these cards automatically, you just set the date once.
              </p>
              <p className="font-body text-sm text-gray-600 mt-0.5">
                Browse the designs below, then{' '}
                <Link to="/signup" className="text-sage underline hover:text-navy transition-colors">
                  start your free trial
                </Link>{' '}
                to send real printed cards to the people you love.
              </p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-navy mb-2">Card Design Library</h1>
          <p className="font-body text-sm text-gray-500">
            Every design is printed on premium cardstock and mailed directly to their door.
            Choose one for an occasion or let us match the perfect style automatically.
          </p>
        </div>

        {/* Occasion tabs — horizontal scroll */}
        <div className="flex gap-2 overflow-x-auto pb-3 mb-5 -mx-4 px-4 sm:mx-0 sm:px-0">
          {OCCASION_TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setOccasionTab(tab.key)}
              className={[
                'flex-shrink-0 px-4 py-2 rounded-full text-xs font-body font-semibold transition-all',
                occasionTab === tab.key
                  ? 'bg-navy text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200',
              ].join(' ')}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Style filter pills */}
        <div className="flex flex-wrap gap-2 mb-6">
          {STYLE_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              onClick={() => setStyleFilter(opt.key)}
              className={[
                'px-3 py-1.5 rounded-full text-xs font-body transition-colors',
                styleFilter === opt.key
                  ? 'bg-sage text-navy'
                  : 'text-gray-500 hover:text-navy hover:bg-white',
              ].join(' ')}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Hint */}
        <p className="font-body text-xs text-gray-400 mb-5 flex items-center gap-1.5">
          <svg className="w-3.5 h-3.5 flex-shrink-0 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
          Click any card to preview the front and see exactly how your message looks printed inside
        </p>

        {/* Design grid */}
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {[...Array(10)].map((_, i) => (
              <div key={i} className="aspect-[3/2] rounded-[10px] bg-gray-100 animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <p className="font-body text-gray-500 text-sm">
              No designs match this filter.{' '}
              <button
                onClick={() => { setStyleFilter('all'); setOccasionTab(''); }}
                className="text-sage hover:underline"
              >
                Clear filters
              </button>
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5">
              {filtered.map((design) => (
                <div
                  key={design.id}
                  className="group flex flex-col items-center gap-2 cursor-pointer"
                  onClick={() => setDetail(design)}
                >
                  <div className="relative">
                    <CardDesignPreview
                      design={design}
                      size="md"
                      showName={false}
                    />
                    {/* Hover overlay hint */}
                    <div className="absolute inset-0 rounded-[9px] bg-navy/0 group-hover:bg-navy/10
                                    transition-colors duration-150 flex items-end justify-center pb-2 pointer-events-none">
                      <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-150
                                       font-body text-[9px] font-semibold text-white bg-navy/70 rounded-full
                                       px-2 py-0.5 backdrop-blur-sm">
                        Preview
                      </span>
                    </div>
                  </div>
                  <div className="text-center">
                    <p className="font-body text-xs font-semibold text-navy leading-tight">{design.name}</p>
                    <p className="font-body text-[10px] text-gray-400 capitalize">{design.style}</p>
                  </div>
                </div>
              ))}
            </div>

            <p className="mt-8 text-center font-body text-xs text-gray-400">
              {filtered.length} design{filtered.length !== 1 ? 's' : ''} available
            </p>
          </>
        )}

        {/* CTA */}
        <div className="mt-12 bg-navy rounded-[24px] p-8 text-center">
          <p className="font-heading text-xl font-bold text-white mb-2">
            Ready to send something real?
          </p>
          <p className="font-body text-sm text-white/60 mb-5">
            {user
              ? 'Add a contact, pick an occasion, and we\'ll handle the printing and mailing automatically.'
              : 'Join MomentSent and start sending real printed cards, automatically. Printing & postage included.'}
          </p>
          <Link
            to={user ? '/dashboard' : '/signup'}
            className="inline-block px-8 py-3 rounded-full bg-[#B59518] text-[#1A1410]
                       font-body font-semibold text-sm hover:bg-[#B59518]/90 transition-colors"
          >
            {user ? 'Go to dashboard →' : 'Start your free trial →'}
          </Link>
        </div>
      </div>

      {!user && <Footer />}

      {/* Design detail overlay */}
      {detail && (
        <DesignDetail
          design={detail}
          cardGreeting={cardGreeting}
          cardClosing={cardClosing}
          cardFromLine={cardFromLine}
          onClose={() => setDetail(null)}
        />
      )}
    </div>
  );
}
